import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Send, Car, Paperclip, X, FileText, Check, CheckCheck, Bell, BellOff, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { inquiriesApi, mediaApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import notificationService from '@/lib/notifications';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminInquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const lastMessageCountRef = useRef(0);

  useEffect(() => {
    // Initialize notifications
    notificationService.init();
    checkNotificationPermission();
    setSoundEnabled(notificationService.isSoundEnabled());
    
    loadInquiry();
    
    // Auto-refresh every 5 seconds for real-time chat
    refreshIntervalRef.current = setInterval(() => {
      loadInquiry(true);
    }, 5000);
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [inquiry?.messages?.length]);

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const enableNotifications = async () => {
    const granted = await notificationService.requestPermission();
    setNotificationsEnabled(granted);
    if (granted) {
      toast.success('Benachrichtigungen aktiviert!');
    } else {
      toast.error('Benachrichtigungen wurden abgelehnt');
    }
  };

  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    notificationService.setSoundEnabled(newValue);
    toast.success(newValue ? 'Ton aktiviert' : 'Ton deaktiviert');
  };

  const loadInquiry = async (silent = false) => {
    try {
      const response = await inquiriesApi.getById(id);
      const newInquiry = response.data;
      
      // Check for new messages and notify
      if (silent && newInquiry.messages && lastMessageCountRef.current > 0) {
        const newCount = newInquiry.messages.length;
        if (newCount > lastMessageCountRef.current) {
          // Find new customer messages
          const newMessages = newInquiry.messages.slice(lastMessageCountRef.current);
          const newCustomerMessages = newMessages.filter(m => m.sender === 'customer');
          
          if (newCustomerMessages.length > 0 && notificationsEnabled) {
            const lastMsg = newCustomerMessages[newCustomerMessages.length - 1];
            notificationService.showNotification(
              `Neue Nachricht von ${newInquiry.customer_name}`,
              lastMsg.message.substring(0, 100),
              `/admin/inquiries/${id}`,
              id
            );
          }
        }
      }
      
      lastMessageCountRef.current = newInquiry.messages?.length || 0;
      setInquiry(newInquiry);
    } catch (error) {
      if (!silent) {
        toast.error('Anfrage nicht gefunden');
        navigate('/admin/inquiries');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(file => mediaApi.upload(file));
      const responses = await Promise.all(uploadPromises);
      const newAttachments = responses.map(r => ({
        url: r.data.url,
        name: r.data.original_name,
        type: r.data.mime_type
      }));
      setAttachments(prev => [...prev, ...newAttachments]);
      toast.success(`${files.length} Datei(en) hochgeladen`);
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() && attachments.length === 0) return;

    setSending(true);
    try {
      const response = await inquiriesApi.reply(id, { 
        reply_message: replyMessage || '(Anhang)',
        attachments: attachments.map(a => a.url)
      });
      setInquiry(response.data);
      lastMessageCountRef.current = response.data.messages?.length || 0;
      setReplyMessage('');
      setAttachments([]);
      toast.success('Antwort gesendet');
    } catch (error) {
      toast.error('Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const isImage = (url) => {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  };

  const renderReadStatus = (msg) => {
    if (msg.sender === 'admin') {
      if (msg.read_by_customer) {
        return <CheckCheck size={14} className="text-blue-500" />;
      } else {
        return <Check size={14} className="text-white/50" />;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner" />
      </div>
    );
  }

  if (!inquiry) {
    return null;
  }

  return (
    <div data-testid="admin-inquiry-detail" className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/admin/inquiries')}
            className="inline-flex items-center gap-2 text-[#666666] hover:text-[#0A0A0A]"
          >
            <ArrowLeft size={16} />
            <span className="text-sm">Zurück</span>
          </button>
          
          {/* Notification & Sound Toggles */}
          <div className="flex gap-2">
            <button
              onClick={toggleSound}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
                soundEnabled 
                  ? 'bg-blue-50 text-blue-700' 
                  : 'bg-[#F9F9F7] text-[#666666] hover:bg-[#E5E5E5]'
              }`}
              title={soundEnabled ? 'Ton aktiviert' : 'Ton deaktiviert'}
              data-testid="sound-toggle"
            >
              {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <button
              onClick={enableNotifications}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
                notificationsEnabled 
                  ? 'bg-green-50 text-green-700' 
                  : 'bg-[#F9F9F7] text-[#666666] hover:bg-[#E5E5E5]'
              }`}
              title={notificationsEnabled ? 'Benachrichtigungen aktiv' : 'Benachrichtigungen aktivieren'}
            >
              {notificationsEnabled ? <Bell size={16} /> : <BellOff size={16} />}
              <span className="hidden sm:inline">
                {notificationsEnabled ? 'Aktiv' : 'Aktivieren'}
              </span>
            </button>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <h1 className="font-heading text-2xl md:text-3xl font-light">{inquiry.customer_name}</h1>
          <span className={`self-start ${inquiry.status === 'new' ? 'status-new' : 'status-replied'}`}>
            {inquiry.status === 'new' ? 'Neu' : 'Beantwortet'}
          </span>
        </div>
        <p className="text-sm text-[#666666] mt-1">{inquiry.customer_email}</p>
        {inquiry.car_info && (
          <Link 
            to={`/cars/${inquiry.car_id}`}
            target="_blank"
            className="inline-flex items-center gap-2 text-sm text-gold hover:underline mt-2"
          >
            <Car size={14} />
            {inquiry.car_info.year} {inquiry.car_info.brand} {inquiry.car_info.model}
          </Link>
        )}
      </div>

      {/* Chat Container */}
      <div className="bg-white border border-[#E5E5E5] flex flex-col" style={{ height: 'calc(100vh - 300px)', minHeight: '400px' }}>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {inquiry.messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[85%] sm:max-w-[70%] rounded-lg p-4 ${
                  msg.sender === 'admin' 
                    ? 'text-[#0A0A0A]' 
                    : 'bg-[#F9F9F7] text-[#0A0A0A]'
                }`}
                style={msg.sender === 'admin' ? {
                  backgroundColor: settings?.design_accent_color 
                    ? `${settings.design_accent_color}30` 
                    : '#C8A96820',
                  borderLeft: `3px solid ${settings?.design_accent_color || '#C8A968'}`
                } : undefined}
                data-testid={`message-${index}`}
              >
                <p className="whitespace-pre-wrap break-words text-sm">{msg.message}</p>
                
                {msg.attachments && msg.attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {msg.attachments.map((url, i) => (
                      <a
                        key={i}
                        href={getFullUrl(url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        {isImage(url) ? (
                          <img 
                            src={getFullUrl(url)} 
                            alt="Anhang" 
                            className="max-w-full max-h-48 rounded"
                          />
                        ) : (
                          <span className="flex items-center gap-2 text-sm text-gold underline">
                            <FileText size={14} />
                            Anhang öffnen
                          </span>
                        )}
                      </a>
                    ))}
                  </div>
                )}
                
                <div className={`flex items-center gap-2 mt-2 ${msg.sender === 'admin' ? 'justify-end' : ''}`}>
                  <p className={`text-xs ${msg.sender === 'admin' ? 'text-[#666666]' : 'text-[#999999]'}`}>
                    {formatDate(msg.timestamp)}
                  </p>
                  {renderReadStatus(msg)}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Attachment Preview */}
        {attachments.length > 0 && (
          <div className="px-4 py-2 border-t border-[#E5E5E5] bg-[#F9F9F7]">
            <div className="flex flex-wrap gap-2">
              {attachments.map((att, index) => (
                <div key={index} className="relative group">
                  {isImage(att.url) ? (
                    <img 
                      src={getFullUrl(att.url)} 
                      alt={att.name}
                      className="h-16 w-16 object-cover rounded"
                    />
                  ) : (
                    <div className="h-16 w-16 bg-white rounded flex items-center justify-center border border-[#E5E5E5]">
                      <FileText size={24} className="text-[#666666]" />
                    </div>
                  )}
                  <button
                    onClick={() => removeAttachment(index)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reply Input */}
        <form onSubmit={handleReply} className="border-t border-[#E5E5E5] p-4">
          <div className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/*,.pdf"
              multiple
              className="hidden"
              data-testid="attachment-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="p-3 border border-[#E5E5E5] hover:border-gold hover:text-gold transition-colors flex-shrink-0"
              data-testid="attachment-btn"
            >
              {uploading ? <div className="spinner w-5 h-5" /> : <Paperclip size={20} />}
            </button>
            <input
              type="text"
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              className="flex-1 border border-[#E5E5E5] px-4 py-3 focus:border-[#0A0A0A] focus:outline-none"
              placeholder="Nachricht schreiben..."
              data-testid="reply-input"
            />
            <Button
              type="submit"
              className="btn-primary px-4 flex-shrink-0"
              disabled={sending || (!replyMessage.trim() && attachments.length === 0)}
              data-testid="send-btn"
            >
              {sending ? <div className="spinner w-5 h-5" /> : <Send size={20} />}
            </Button>
          </div>
        </form>
      </div>
      
      <p className="text-xs text-[#999999] text-center mt-2">
        Automatische Aktualisierung alle 30 Sekunden
      </p>
    </div>
  );
}
